import { FormEvent, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { CollapsibleFormSection } from '../../../components/ui/CollapsibleFormSection';
import { DeleteIconButton } from '../../../components/ui/DeleteIconButton';
import { EditIconButton } from '../../../components/ui/EditIconButton';
import { FormActionBar } from '../../../components/ui/FormActionBar';
import { InlineAlert } from '../../../components/ui/InlineAlert';
import { useMessageBox } from '../../../contexts/MessageBoxContext';
import { useCollapsibleItemForm } from '../../../hooks/useCollapsibleItemForm';
import {
  EvaluationCycle,
  EvaluationCycleCalendar,
  EvaluationCycleCalendarUpsertPayload,
  createCycleCalendar,
  deleteCycleCalendar,
  listCycleCalendars,
  updateCycleCalendar,
} from '../../../services/peoplePerformanceApi';
import {
  fromLocalDateTimeInputValue,
  toLocalDateTimeInputValue,
} from '../../../utils/localDateTimeInput';

type CalendarFormState = {
  name: string;
  periodStartLocal: string;
  periodEndLocal: string;
  evaluationCollectionStartLocal: string;
  evaluationCollectionEndLocal: string;
  feedbackCollectionStartLocal: string;
  feedbackCollectionEndLocal: string;
  sortOrder: number;
};

function defaultCalendarForm(sortOrder = 0): CalendarFormState {
  const start = new Date();
  const end = new Date();
  end.setMonth(end.getMonth() + 1);
  const localStart = toLocalDateTimeInputValue(start.toISOString());
  const localEnd = toLocalDateTimeInputValue(end.toISOString());
  return {
    name: '',
    periodStartLocal: localStart,
    periodEndLocal: localEnd,
    evaluationCollectionStartLocal: localStart,
    evaluationCollectionEndLocal: localEnd,
    feedbackCollectionStartLocal: localStart,
    feedbackCollectionEndLocal: localEnd,
    sortOrder,
  };
}

type EvaluationCycleCalendarsSectionProps = {
  cycleId: string;
  cycle: EvaluationCycle | null;
  canWrite: boolean;
  onCountChange?: (count: number) => void;
  onCycleRefresh?: () => void;
};

export function EvaluationCycleCalendarsSection({
  cycleId,
  cycle,
  canWrite,
  onCountChange,
  onCycleRefresh,
}: EvaluationCycleCalendarsSectionProps) {
  const { confirmDelete } = useMessageBox();
  const {
    formExpanded,
    expandForm,
    collapseForm,
    handleCreateSuccess,
    toggleOrCancel,
  } = useCollapsibleItemForm();
  const [calendars, setCalendars] = useState<EvaluationCycleCalendar[]>([]);
  const [loading, setLoading] = useState(true);
  const [calendarForm, setCalendarForm] = useState<CalendarFormState>(defaultCalendarForm());
  const [editingCalendarId, setEditingCalendarId] = useState<string | null>(null);
  const [savingCalendar, setSavingCalendar] = useState(false);

  const canEdit = canWrite && cycle?.status === 'Draft';

  async function loadCalendars(): Promise<void> {
    setLoading(true);
    try {
      const result = await listCycleCalendars(cycleId);
      setCalendars(result);
      onCountChange?.(result.length);
    } catch {
      toast.error('Falha ao carregar calendários.');
      setCalendars([]);
      onCountChange?.(0);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void loadCalendars();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cycleId]);

  function resetCalendarForm(): void {
    setEditingCalendarId(null);
    setCalendarForm(defaultCalendarForm(calendars.length));
  }

  function startEditCalendar(item: EvaluationCycleCalendar): void {
    expandForm();
    setEditingCalendarId(item.id);
    setCalendarForm({
      name: item.name,
      periodStartLocal: toLocalDateTimeInputValue(item.periodStartUtc),
      periodEndLocal: toLocalDateTimeInputValue(item.periodEndUtc),
      evaluationCollectionStartLocal: toLocalDateTimeInputValue(item.evaluationCollectionStartUtc),
      evaluationCollectionEndLocal: toLocalDateTimeInputValue(item.evaluationCollectionEndUtc),
      feedbackCollectionStartLocal: toLocalDateTimeInputValue(item.feedbackCollectionStartUtc),
      feedbackCollectionEndLocal: toLocalDateTimeInputValue(item.feedbackCollectionEndUtc),
      sortOrder: item.sortOrder,
    });
  }

  async function handleSaveCalendar(event: FormEvent): Promise<void> {
    event.preventDefault();
    if (!canEdit || !cycle) return;

    const periodStartUtc = fromLocalDateTimeInputValue(calendarForm.periodStartLocal);
    const periodEndUtc = fromLocalDateTimeInputValue(calendarForm.periodEndLocal);
    const evaluationCollectionStartUtc = fromLocalDateTimeInputValue(calendarForm.evaluationCollectionStartLocal);
    const evaluationCollectionEndUtc = fromLocalDateTimeInputValue(calendarForm.evaluationCollectionEndLocal);
    const feedbackCollectionStartUtc = fromLocalDateTimeInputValue(calendarForm.feedbackCollectionStartLocal);
    const feedbackCollectionEndUtc = fromLocalDateTimeInputValue(calendarForm.feedbackCollectionEndLocal);

    if (
      !periodStartUtc
      || !periodEndUtc
      || !evaluationCollectionStartUtc
      || !evaluationCollectionEndUtc
      || (cycle.enableCycleFeedback && (!feedbackCollectionStartUtc || !feedbackCollectionEndUtc))
    ) {
      toast.error('Informe datas válidas em todos os campos do calendário.');
      return;
    }

    setSavingCalendar(true);
    try {
      const payload: EvaluationCycleCalendarUpsertPayload = {
        name: calendarForm.name,
        periodStartUtc,
        periodEndUtc,
        evaluationCollectionStartUtc,
        evaluationCollectionEndUtc,
        feedbackCollectionStartUtc: cycle.enableCycleFeedback ? feedbackCollectionStartUtc : null,
        feedbackCollectionEndUtc: cycle.enableCycleFeedback ? feedbackCollectionEndUtc : null,
        sortOrder: calendarForm.sortOrder,
      };
      if (editingCalendarId) {
        await updateCycleCalendar(cycleId, editingCalendarId, payload);
        toast.success('Calendário atualizado.');
        resetCalendarForm();
      } else {
        await createCycleCalendar(cycleId, payload);
        toast.success('Calendário criado.');
        handleCreateSuccess(resetCalendarForm);
      }
      await loadCalendars();
      onCycleRefresh?.();
    } catch {
      toast.error('Falha ao salvar calendário.');
    } finally {
      setSavingCalendar(false);
    }
  }

  async function handleDeleteCalendar(id: string): Promise<void> {
    if (!canEdit) return;
    if (
      !(await confirmDelete({
        title: 'Remover calendário',
        message: 'Remover este calendário de avaliação?',
        confirmLabel: 'Remover',
      }))
    ) {
      return;
    }
    try {
      await deleteCycleCalendar(cycleId, id);
      toast.success('Calendário removido.');
      if (editingCalendarId === id) {
        collapseForm(resetCalendarForm);
      }
      await loadCalendars();
      onCycleRefresh?.();
    } catch {
      toast.error('Falha ao remover calendário.');
    }
  }

  return (
    <section className="mb-4 rounded-xl border border-secondary-200 bg-white p-4">
      <div className="mb-3">
        <h2 className="text-lg font-semibold text-secondary-900">Calendários de avaliação</h2>
        <p className="text-sm text-secondary-500">
          Um ciclo pode ter zero ou vários períodos de coleta (ex.: meio do ano e fim do ano, ou um a cada trimestre).
        </p>
      </div>

      {loading ? (
        <p className="text-sm text-secondary-500">Carregando calendários...</p>
      ) : calendars.length === 0 ? (
        <InlineAlert className="mb-4">
          Nenhum calendário. Sem calendários, o menu Desempenho não exibe formulários deste ciclo.
        </InlineAlert>
      ) : (
        <div className="mb-4 overflow-x-auto">
          <table className="erp-table">
            <thead>
              <tr>
                <th>Nome</th>
                <th>Período</th>
                <th>Coleta avaliações</th>
                <th>Coleta feedbacks</th>
                {canEdit ? <th>Ações</th> : null}
              </tr>
            </thead>
            <tbody>
              {calendars.map((item) => (
                <tr key={item.id}>
                  <td className="font-medium text-secondary-900">{item.name}</td>
                  <td className="text-sm text-secondary-600">
                    {new Date(item.periodStartUtc).toLocaleDateString('pt-BR')}
                    {' — '}
                    {new Date(item.periodEndUtc).toLocaleDateString('pt-BR')}
                  </td>
                  <td className="text-sm text-secondary-600">
                    {new Date(item.evaluationCollectionStartUtc).toLocaleDateString('pt-BR')}
                    {' — '}
                    {new Date(item.evaluationCollectionEndUtc).toLocaleDateString('pt-BR')}
                  </td>
                  <td className="text-sm text-secondary-600">
                    {cycle?.enableCycleFeedback && item.feedbackCollectionStartUtc && item.feedbackCollectionEndUtc
                      ? `${new Date(item.feedbackCollectionStartUtc).toLocaleDateString('pt-BR')} — ${new Date(item.feedbackCollectionEndUtc).toLocaleDateString('pt-BR')}`
                      : '—'}
                  </td>
                  {canEdit ? (
                    <td>
                      <div className="flex items-center gap-1">
                        <EditIconButton label="Editar calendário" onClick={() => startEditCalendar(item)} />
                        <DeleteIconButton
                          label="Remover calendário"
                          onClick={() => void handleDeleteCalendar(item.id)}
                        />
                      </div>
                    </td>
                  ) : null}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {canEdit ? (
        <CollapsibleFormSection
          title={editingCalendarId ? 'Editar calendário' : 'Novo calendário'}
          expanded={formExpanded}
          onToggle={() => toggleOrCancel(resetCalendarForm)}
          isEditing={!!editingCalendarId}
          createSummary="Clique para expandir e cadastrar um novo calendário de coleta"
          className="mt-4 mb-0"
        >
          <form onSubmit={(event) => void handleSaveCalendar(event)} className="erp-form px-4 pb-4 pt-3">
            <div className="erp-form-grid">
              <label className="erp-label sm:col-span-2">
                Nome
                <input
                  className="erp-input"
                  value={calendarForm.name}
                  onChange={(event) => setCalendarForm({ ...calendarForm, name: event.target.value })}
                  placeholder="Ex.: Meio do ano, Q1, Fim do ano"
                  required
                />
              </label>
              <label className="erp-label">
                Período início
                <input
                  type="datetime-local"
                  className="erp-input"
                  value={calendarForm.periodStartLocal}
                  onChange={(event) =>
                    setCalendarForm({ ...calendarForm, periodStartLocal: event.target.value })
                  }
                  required
                />
              </label>
              <label className="erp-label">
                Período fim
                <input
                  type="datetime-local"
                  className="erp-input"
                  value={calendarForm.periodEndLocal}
                  onChange={(event) =>
                    setCalendarForm({ ...calendarForm, periodEndLocal: event.target.value })
                  }
                  required
                />
              </label>
              <label className="erp-label">
                Coleta avaliações início
                <input
                  type="datetime-local"
                  className="erp-input"
                  value={calendarForm.evaluationCollectionStartLocal}
                  onChange={(event) =>
                    setCalendarForm({
                      ...calendarForm,
                      evaluationCollectionStartLocal: event.target.value,
                    })
                  }
                  required
                />
              </label>
              <label className="erp-label">
                Coleta avaliações fim
                <input
                  type="datetime-local"
                  className="erp-input"
                  value={calendarForm.evaluationCollectionEndLocal}
                  onChange={(event) =>
                    setCalendarForm({
                      ...calendarForm,
                      evaluationCollectionEndLocal: event.target.value,
                    })
                  }
                  required
                />
              </label>
              {cycle?.enableCycleFeedback ? (
                <>
                  <label className="erp-label">
                    Coleta feedbacks início
                    <input
                      type="datetime-local"
                      className="erp-input"
                      value={calendarForm.feedbackCollectionStartLocal}
                      onChange={(event) =>
                        setCalendarForm({
                          ...calendarForm,
                          feedbackCollectionStartLocal: event.target.value,
                        })
                      }
                      required
                    />
                  </label>
                  <label className="erp-label">
                    Coleta feedbacks fim
                    <input
                      type="datetime-local"
                      className="erp-input"
                      value={calendarForm.feedbackCollectionEndLocal}
                      onChange={(event) =>
                        setCalendarForm({
                          ...calendarForm,
                          feedbackCollectionEndLocal: event.target.value,
                        })
                      }
                      required
                    />
                  </label>
                </>
              ) : null}
            </div>
            <FormActionBar onCancel={() => collapseForm(resetCalendarForm)} className="erp-btn-actions mt-4">
              <button type="submit" className="erp-btn-primary" disabled={savingCalendar}>
                {savingCalendar ? 'Salvando...' : editingCalendarId ? 'Atualizar' : 'Criar'}
              </button>
            </FormActionBar>
          </form>
        </CollapsibleFormSection>
      ) : null}
    </section>
  );
}
